/**
 * Szablony · N / Plany klientów · M — which of the two lists the master pane is showing.
 *
 * ── A radio group, NOT an ARIA tablist, and that is the roster's rule taken deliberately ─────
 * `role="tablist"` promises a keyboard contract this control does not keep: arrows move AND
 * activate, and the set holds one tab stop. Both are wrong here, because switching the scope is a
 * NAVIGATION — it rewrites the address and swaps the list under a selection — and a control that
 * navigates on an arrow key is a control a keyboard user cannot pass without loading two screens.
 * A radio group promises exactly what this is, one choice out of two, and gets the same single tab
 * stop for free. `RosterToolbar` states the same rule for its four filter tabs.
 *
 * Plain elements, for the reason that file gives: a radio group is one of the very few controls the
 * platform already gets right, and a wrapper that only restyles it is a pass-through.
 *
 * ── A count that is not known is not drawn, and never drawn as a zero ────────────────────────
 * `Plany klientów` reads its figure from `GET /plan-assignment`, which arrives in wave 3. Until
 * then it has no count at all rather than a `0` — the same rule the header's em dash follows, and
 * for the same reason: `Plany klientów · 0` beside a list nobody has read yet is a statement, and a
 * false one for every trainer who has assigned a plan.
 */

import { useTranslation } from 'react-i18next';

import type { PlanScope } from '../model/planScope';
import styles from './ScopeSwitch.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';
import rule from '~/shared/ui/tabRule.module.css';

interface ScopeSwitchProps {
  readonly scope: PlanScope;
  readonly onScopeChange: (scope: PlanScope) => void;
  /** `null` where the figure is not known yet — see the header. */
  readonly templateCount: number | null;
  readonly clientPlanCount: number | null;
}

const OPTIONS: readonly { readonly scope: PlanScope; readonly labelKey: string }[] = [
  { scope: 'templates', labelKey: 'web:planLibrary.scope.templates' },
  { scope: 'clients', labelKey: 'web:planLibrary.scope.clientPlans' },
];

export function ScopeSwitch({
  scope,
  onScopeChange,
  templateCount,
  clientPlanCount,
}: ScopeSwitchProps) {
  const { t } = useTranslation();
  const counts: Readonly<Record<PlanScope, number | null>> = {
    templates: templateCount,
    clients: clientPlanCount,
  };

  return (
    <fieldset className={styles.track}>
      {/* The group is NAMED for assistive technology and drawn by the options themselves. Hidden
        * rather than `display: none`, which would take the name out of the accessibility tree. */}
      <legend className={hidden.srOnly}>{t('web:planLibrary.scope.legend')}</legend>
      {OPTIONS.map((option) => {
        const selected = option.scope === scope;
        const count = counts[option.scope];
        return (
          <label
            key={option.scope}
            className={`${styles.option} ${selected ? styles.optionSelected : ''}`}
          >
            <input
              type="radio"
              name="plan-library-scope"
              className={styles.input}
              value={option.scope}
              checked={selected}
              onChange={() => {
                onScopeChange(option.scope);
              }}
            />
            {t(option.labelKey)}
            {/* ALWAYS RENDERED, and hidden rather than absent while the figure is unknown. `.option`
              * centres its content, so a pill that mounted when a read landed slid the WORD beside
              * it by half the pill and half the gap — twice on a cold entry, once per read. The
              * element holds its box from the first frame and only its ink arrives late.
              *
              * NOT `aria-hidden`. The option's accessible name is its own content, and hiding the
              * figure would leave a control named "Szablony" wearing the visible label
              * "Szablony 8" — which fails 2.5.3, and takes the one number on the switch away from
              * a listener for nothing. `visibility: hidden` takes the empty box out of the
              * accessibility tree for free, so the name is "Szablony" until the count exists. */}
            <span className={`${styles.count} ${count === null ? styles.countWaiting : ''}`}>
              {count === null ? null : <b className={styles.countFigure}>{count}</b>}
            </span>
            {/* The app's one tab underline — `shared/ui/tabRule.module.css` for the treatment and
              * the movement, this stylesheet's `.optionRule` for the box. The chosen state is a
              * second class on the same element, never a descendant of `.optionSelected`, so the
              * shared module depends on no class it does not own. */}
            <i
              className={`${String(rule.rule)} ${String(styles.optionRule)} ${
                selected ? String(rule.ruleOn) : ''
              }`}
              aria-hidden="true"
            />
          </label>
        );
      })}
    </fieldset>
  );
}
